import { supabase } from '../lib/supabase';

/**
 * Call Service
 * Service for managing call data and operations
 */

// Get user calls with advanced filtering
export const getUserCalls = async (userId, options = {}) => {
  const {
    search = null,
    status = null,
    success = null,
    dateFrom = null,
    dateTo = null,
    sortBy = 'call_started_at',
    sortOrder = 'desc',
    page = 1,
    pageSize = 10
  } = options;

  try {
    const offset = (page - 1) * pageSize;

    const { data, error } = await supabase
      .rpc('get_user_calls_filtered', {
        target_user_id: userId,
        search_term: search,
        status_filter: status,
        success_filter: success,
        date_from: dateFrom,
        date_to: dateTo,
        sort_by: sortBy,
        sort_order: sortOrder,
        page_limit: pageSize,
        page_offset: offset
      });

    if (error) {
      throw new Error(`Failed to fetch calls: ${error.message}`);
    }

    // Get total count for pagination
    let totalCount = 0;
    if (data && data.length > 0) {
      const { count, error: countError } = await supabase
        .from('call_reports')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId);

      if (!countError) {
        totalCount = count;
      }
    }

    return {
      data: data || [],
      totalCount,
      totalPages: Math.ceil(totalCount / pageSize),
      currentPage: page,
      pageSize,
      error: null
    };

  } catch (error) {
    console.error('Error fetching user calls:', error);
    return {
      data: [],
      totalCount: 0,
      totalPages: 0,
      currentPage: page,
      pageSize,
      error: error.message
    };
  }
};

// Get single call details
export const getCallDetails = async (callId) => {
  try {
    const { data, error } = await supabase
      .from('call_reports')
      .select('*')
      .eq('call_id', callId)
      .single();

    if (error) {
      throw new Error(`Failed to fetch call details: ${error.message}`);
    }

    return { data, error: null };
  } catch (error) {
    console.error('Error fetching call details:', error);
    return { data: null, error: error.message };
  }
};

// Format call duration
export const formatDuration = (minutes) => {
  if (!minutes || minutes < 0) return '0m';
  
  const totalMinutes = Math.round(minutes);
  
  if (totalMinutes < 60) {
    return `${totalMinutes}m`;
  }
  
  const hours = Math.floor(totalMinutes / 60);
  const remainingMinutes = totalMinutes % 60;
  
  if (remainingMinutes === 0) {
    return `${hours}h`;
  }
  
  return `${hours}h ${remainingMinutes}m`;
};

// Format call cost
export const formatCost = (cost) => {
  if (!cost || cost === 0) return '$0.00';
  
  const numericCost = parseFloat(cost);
  if (isNaN(numericCost)) return '$0.00';
  
  return `$${numericCost.toFixed(2)}`;
};

// Format call date
export const formatCallDate = (dateString) => {
  if (!dateString) return 'N/A';
  
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return 'N/A';
  
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Get call status color classes
export const getCallStatusColor = (status, success) => {
  if (success === true) {
    return 'bg-green-100 text-green-800 border-green-200';
  } else if (success === false) {
    return 'bg-red-100 text-red-800 border-red-200';
  }
  
  switch (status?.toLowerCase()) {
    case 'completed':
      return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'in-progress':
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    case 'failed':
      return 'bg-red-100 text-red-800 border-red-200';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

// Get call success display text
export const getCallSuccessText = (success, endedReason) => {
  if (success === true) return 'Successful';
  if (success === false) return endedReason || 'Failed';
  return 'Unknown';
};

// Calculate call performance score
export const getCallPerformanceScore = (call) => {
  let score = 0;
  let factors = 0;
  
  // Success factor (40% weight)
  if (call.success_evaluation !== null) {
    score += call.success_evaluation ? 40 : 0;
    factors += 40;
  }
  
  // Duration factor (20% weight) - sweet spot is 5-15 minutes
  if (call.duration_minutes) {
    const duration = call.duration_minutes;
    let durationScore = 0;
    
    if (duration >= 5 && duration <= 15) {
      durationScore = 20; // Perfect duration
    } else if (duration >= 2 && duration < 5) {
      durationScore = 15; // Good duration
    } else if (duration > 15 && duration <= 30) {
      durationScore = 15; // Acceptable duration
    } else if (duration > 30) {
      durationScore = 10; // Long call
    } else {
      durationScore = 5; // Very short call
    }
    
    score += durationScore;
    factors += 20;
  }
  
  // Latency factor (20% weight) - lower is better
  if (call.model_latency_average !== null) {
    const latency = call.model_latency_average;
    let latencyScore = 0;
    
    if (latency <= 1000) {
      latencyScore = 20; // Excellent latency
    } else if (latency <= 2000) {
      latencyScore = 15; // Good latency
    } else if (latency <= 3000) {
      latencyScore = 10; // Fair latency
    } else {
      latencyScore = 5; // Poor latency
    }
    
    score += latencyScore;
    factors += 20;
  }
  
  // Cost efficiency factor (20% weight)
  if (call.total_cost !== null && call.duration_minutes > 0) {
    const costPerMinute = call.total_cost / call.duration_minutes;
    let costScore = 0;
    
    if (costPerMinute <= 0.1) {
      costScore = 20; // Very cost effective
    } else if (costPerMinute <= 0.2) {
      costScore = 15; // Cost effective
    } else if (costPerMinute <= 0.5) {
      costScore = 10; // Moderate cost
    } else {
      costScore = 5; // High cost
    }
    
    score += costScore;
    factors += 20;
  }
  
  // Calculate final score as percentage
  return factors > 0 ? Math.round((score / factors) * 100) : 0;
};

// Get performance score color
export const getPerformanceScoreColor = (score) => {
  if (score >= 80) return 'text-green-600';
  if (score >= 60) return 'text-yellow-600';
  if (score >= 40) return 'text-orange-600';
  return 'text-red-600';
};

// Export call data to CSV
export const exportCallsToCSV = (calls) => {
  if (!calls || calls.length === 0) {
    throw new Error('No call data to export');
  }

  const headers = [
    'Call ID',
    'Date/Time',
    'Duration (minutes)',
    'Assistant',
    'Status',
    'Success',
    'Ended Reason',
    'Cost',
    'Model Latency',
    'Voice Latency'
  ];

  const csvContent = [
    headers.join(','),
    ...calls.map(call => [
      call.call_id ?? call.provider_call_id ?? '',
      formatCallDate(call.call_started_at),
      call.duration_minutes || 0,
      call.assistant_name || '',
      call.call_status || '',
      call.success_evaluation !== null ? (call.success_evaluation ? 'Yes' : 'No') : 'Unknown',
      call.ended_reason || '',
      call.total_cost || 0,
      call.model_latency_average || 0,
      call.voice_latency_average || 0
    ].map(field => `"${field}"`).join(','))
  ].join('\n');

  return csvContent;
};

// Download CSV file
export const downloadCallsCSV = (calls, filename = 'user_calls.csv') => {
  try {
    const csvContent = exportCallsToCSV(calls);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  } catch (error) {
    console.error('Error downloading CSV:', error);
    throw error;
  }
};